/* eslint-disable comma-dangle */
import { get } from 'lodash';

import React, { lazy, Suspense, useContext } from 'react';
import PropTypes from 'prop-types';
import { Redirect, Route, Switch } from 'react-router-dom';
import { Button, CircularProgress } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import AuthorizationContext from '../contexts/AuthorizationContext';
import EmployerContext from '../contexts/EmployerContext';
import { CampaignApp } from '../contexts/CampaignContext';
import { RoutePaths } from '../constants/RoutePaths';
import { getDefaultPostLoginRoute } from '../utils/routesUtils';
import {
    NoAccessPage, LoginSSOExpiredPage, SuccessfulActivationPage, CalibrationDisabledPage, CalibrationNotReadyPage, InactiveUserPage,
} from '../modules/common/ExceptionPage';
// Modified by AI on 05/27/2026. Edit #1.
import ErrorBoundary from '../modules/error-boundary/ErrorBoundary';

// Modified by AI on 05/14/2026. Edit #1.
export const ProtectedRoute = ({
    component: RouteComponent = null,
    canAccess = false,
    doExtraCheck = true,
    forSuperUsersOnly = false,
    allowFromBackend = false,
    redirectTo = '/login',
    func = null,
    ...rest
}) => {
    const {
        isTalenyaUser, isTalentExpertUser, isEmployerRepAdmin, logout,
    } = useContext(AuthorizationContext);
    const { employer } = useContext(EmployerContext);
    if (!canAccess && redirectTo === '/login') {
        return logout();
    }

    const isFromBackend = allowFromBackend ? navigator.userAgent.match(/(headless)/i) : null;

    if (doExtraCheck
        && (!isTalenyaUser && !isTalentExpertUser && !isEmployerRepAdmin
            && (forSuperUsersOnly || employer?.settings?.isOnlyFastRatingAllowed)
            && (!allowFromBackend || !isFromBackend)
        )
    ) {
        return <NoAccessPage/>;
    }

    if (func) {
        setTimeout(async () => {
            await func();
        }, 0);
        return null;
    }

    return (
        <Route {...rest}
            render={(props) => (canAccess ? <RouteComponent {...props} />
                : <Redirect to={redirectTo}/>)}/>
    );
};

ProtectedRoute.propTypes = {
    component: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.object,
    ]),
    canAccess: PropTypes.bool,
    doExtraCheck: PropTypes.bool,
    forSuperUsersOnly: PropTypes.bool,
    allowFromBackend: PropTypes.bool,
    redirectTo: PropTypes.string,
    func: PropTypes.func,
};

export const LoginPage = lazy(() => import(
    /* webpackChunkName: "LoginPage" */
    '../modules/login/LoginPage'
));

export const UserActivationPage = lazy(() => import(
    /* webpackChunkName: "UserActivationPage" */
    '../modules/login/UserActivationPage'
));

export const LoginSSOPage = lazy(() => import(
    /* webpackChunkName: "LoginSSOPage" */
    '../modules/login-sso/LoginSSOPage'
));

export const RegistrationPage = lazy(() => import(
    /* webpackChunkName: "RegistrationPage" */
    '../modules/registration/RegistrationPage'
));

export const RegistrationPageStepTwo = lazy(() => import(
    /* webpackChunkName: "RegistrationPageStepTwo" */
    '../modules/registration/RegistrationPageStepTwo'
));

export const EmployerWelcomePage = lazy(() => import(
    /* webpackChunkName: "EmployerWelcomePage" */
    '../modules/employer/welcome/EmployerWelcomePage'
));

export const EmployerCampaignManagerPage = lazy(() => import(
    /* webpackChunkName: "EmployerCampaignManagerPage" */
    '../modules/employer/campaign-manager/CampaignManagerPage'
));

export const EmployerCampaignsActivityPage = lazy(() => import(
    /* webpackChunkName: "EmployerCampaignsActivityPage" */
    '../modules/employer/campaigns-activity/CampaignsActivityPage'
));

export const PCCampaignActivityPage = lazy(() => import(
    '../modules/employer/campaigns-activity/PCCampaignsActivityPage'
));

export const EmployerCampaignActivityPage = lazy(() => import(
    /* webpackChunkName: "EmployerCampaignActivityPage" */
    '../modules/employer/campaign-activity/CampaignActivityPage'
));

export const EmployerRepManageUsersPage = lazy(() => import(
    /* webpackChunkName: "EmployerRepManageUsersPage" */
    '../modules/employer/manage-users/EmployerRepManageUsersPage'
));

export const EmployerRepDiversityReportPage = lazy(() => import(
    /* webpackChunkName: "EmployerRepDiversityReportPage" */
    '../modules/employer/diversity-report/EmployerDiversityReportPage'
));

export const PrintableCandidateProfilePage = lazy(() => import(
    /* webpackChunkName: "PrintableCandidateProfilePage" */
    '../modules/print/PrintCandidateProfileContainer'
));

export const PrintableDiversityOptimizerPage = lazy(() => import(
    /* webpackChunkName: "PrintableDiversityOptimizerPage" */
    '../modules/print/diversity-optimizer/PrintDiversityOptimizerContainer'
));

export const PrintableDiversityReportPage = lazy(() => import(
    /* webpackChunkName: "PrintableDiversityReportPage" */
    '../modules/print/diversity-report/PrintDiversityReportContainer'
));

export const EmployerJobWizardPage = lazy(() => import(
    /* webpackChunkName: "EmployerJobWizardContainer" */
    '../modules/employer/position-wizard/EmployerJobWizardPage'
));

export const EmployerJobWizardResolverPage = lazy(() => import(
    /* webpackChunkName: "EmployerJobWizardResolverPage" */
    '../modules/employer/position-wizard/EmployerJobPageResolver'
));

export const PublicJobDescriptionPage = lazy(() => import(
    /* webpackChunkName: "PublicJobDescriptionPage" */
    '../modules/public/position/PublicJobDescriptionContainer'
));

export const PublicJobApprovalPage = lazy(() => import(
    /* webpackChunkName: "PublicJobApprovalPage" */
    '../modules/public/position/PublicJobApprovalContainer'
));

export const PublicDiversityReportPage = lazy(() => import(
    /* webpackChunkName: "PublicDiversityReportPage" */
    '../modules/employer/diversity-report/DiversityReportPublicView'
));

export const SearchCandidatesPage = lazy(() => import(
    /* webpackChunkName: "SearchCandidatesPage" */
    '../modules/search/candidates/SearchCandidatesPage'
));

export const EmployerPositionPage = lazy(() => import(
    /* webpackChunkName: "EmployerPositionPage" */
    '../modules/employer/position/EmployerPositionPage'
));

export const EmployerPositionsPage = lazy(() => import(
    /* webpackChunkName: "EmployerPositionsPage" */
    '../modules/employer/positions/EmployerPositionsPage'
));

export const UnsubscribeOptionsPage = lazy(() => import(
    /* webpackChunkName: "UnsubscribeOptionsContainer" */
    '../modules/public/position/UnsubscribeOptionsContainer'
));

export const ThankYouPage = lazy(() => import(
    /* webpackChunkName: "ThankYouPage" */
    '../modules/public/position/ThankYouPage'
));

export const EmployerCampaignsPage = lazy(() => import(
    /* webpackChunkName: "EmployerCampaignsPage" */
    '../modules/employer/employer-campaigns/EmployerCampaignPage/EmployerCampaignsPage'
));

export const EmployerCampaignDetailsPage = lazy(() => import(
    /* webpackChunkName: "EmployerCampaignDetailsPage" */
    '../modules/employer/employer-campaigns/EmployerCampaignDetailsPage/EmployerCampaignDetailsPage'
));

export const JobLandingPageContainer = lazy(() => import(
    /* webpackChunkName: "JobLandingPageContainer" */
    '../modules/public/position/job-landing-page/JobLandingPageContainer'
));

export const JobLandingPageDescriptionContainer = lazy(() => import(
    /* webpackChunkName: "JobLandingPageDescription" */
    '../modules/public/position/job-landing-page/job-landing-page-description/JobLandingPageDescriptionContainer'
));

export const UnsubscribePageContainer = lazy(() => import(
    /* webpackChunkName: "UnsubscribePageContainer" */
    '../modules/public/position/unsubscribe-page/UnsubscribePageContainer'
));

export const SystemCampaignDetailsPage = lazy(() => import(
    /* webpackChunkName: "EmployerCampaignDetailsPage" */
    '../modules/system/SystemCampaignDetailsPage'
));

// Generated by AI on 05/12/2026
export const CreateRecruiterPage = lazy(() => import(
    /* webpackChunkName: "CreateRecruiterPage" */
    '../modules/recruiter/create-recruiter/CreateRecruiterPage'
));

// Generated by AI on 06/03/2026
export const EditEmployerPage = lazy(() => import(
    /* webpackChunkName: "EditEmployerPage" */
    '../modules/employer/edit-employer/EditEmployerPage'
));

// Generated by AI on 06/03/2026
export const EditAdvancedSettingsPage = lazy(() => import(
    /* webpackChunkName: "EditAdvancedSettingsPage" */
    '../modules/employer/edit-employer/EditAdvancedSettingsPage'
));

// Modified by AI on 05/13/2026. Edit #4.
export const TalenyaUserPageSecurityMatch = (PageComponent, redirectTo) => {
    // Modified by AI on 05/26/2026. Edit #1.
    const TalenyaUserPageComponent = ({
        history = {},
        location = {},
        match = {},
        ...restProps
    }) => {
        const {
            availableEmployerIds,
            isTalenyaUser,
            employerRep,
            user,
        } = useContext(AuthorizationContext);

        const defaultRedirectTo = getDefaultPostLoginRoute({ user, employerRep, availableEmployerIds }) || RoutePaths.noAccessPage;

        if (isTalenyaUser) {
            return <PageComponent history={history} location={location} match={match} {...restProps} />;
        }
        return <Redirect to={redirectTo || defaultRedirectTo}/>;
    };

    TalenyaUserPageComponent.propTypes = {
        history: PropTypes.object,
        location: PropTypes.object,
        match: PropTypes.object,
    };

    return TalenyaUserPageComponent;
};

// Generated by AI on 10/27/2026
// Modified by AI on 10/28/2026. Edit #2.
export const CreateRecruiterPageSecurityMatch = (PageComponent) => {
    const CreateRecruiterPageComponent = (props) => {
        const { t } = useTranslation();
        const {
            availableEmployerIds,
            isTalenyaUser,
            isUserAllowedToCreateRecruiters,
            isPostLoginPreferencesPending,
            isCreateRecruiterPermissionError,
            retryCreateRecruiterPermission,
            employerRep,
            user,
        } = useContext(AuthorizationContext);
        const defaultRedirectTo = getDefaultPostLoginRoute({ user, employerRep, availableEmployerIds }) || RoutePaths.noAccessPage;

        if (!isTalenyaUser) {
            return <Redirect to={defaultRedirectTo}/>;
        }

        if (isUserAllowedToCreateRecruiters) {
            return <PageComponent {...props} />;
        }

        if (isPostLoginPreferencesPending) {
            return <CircularProgress size={24} aria-label={t('LOADING')}/>;
        }

        if (isCreateRecruiterPermissionError) {
            return <div role='alert'>
                <p>{t('DEFAULT_NOTIFICATION_ERROR')}</p>
                <Button onClick={() => retryCreateRecruiterPermission({ cancelRefetch: false })}>{t('RETRY')}</Button>
            </div>;
        }

        return <Redirect to={defaultRedirectTo}/>;
    };

    return CreateRecruiterPageComponent;
};

// Modified by AI on 10/27/2026. Edit #1.
const CreateRecruiterPageForAllowedUsers = CreateRecruiterPageSecurityMatch(CreateRecruiterPage);

if (ENVIRONMENT === 'local') {
    Route.propTypes = get(Route, 'propTypes', {});
    Route.propTypes.component = PropTypes.oneOfType([
        get(Route, 'propTypes.component', PropTypes.func),
        PropTypes.object,
    ]);
}

export const employerPositionsPath = RoutePaths.employerPositions;

// Modified by AI on 05/27/2026. Edit #2.
const ApplicationRoutes = () => {
    const { user, isUserAllowedToEditSystemCampaigns, logout } = useContext(AuthorizationContext);
    const isUserAuthorized = !!user;

    return (
        <ErrorBoundary>
            <Suspense fallback={<div className={'test'}/>}>
                <Switch>
                    <ProtectedRoute
                        exact={true}
                        canAccess={true}
                        path='/login'
                        component={LoginPage}
                    />
                    <ProtectedRoute
                        exact={true}
                        canAccess={true}
                        path='/logout'
                        component={null}
                        func={logout}
                    />
                    <ProtectedRoute
                        exact={true}
                        canAccess={true}
                        path='/loginsso'
                        component={LoginSSOPage}
                    />
                    <ProtectedRoute
                        exact={true}
                        canAccess={true}
                        doExtraCheck={false}
                        path='/public/employerRep/:employerRepId/sign-up/details/true/:invitationId'
                        component={RegistrationPage}
                    />
                    <ProtectedRoute
                        exact={true}
                        canAccess={true}
                        doExtraCheck={false}
                        path='/public/employerRep/:employerRepId/sign-up/details/true'
                        component={RegistrationPage}
                    />
                    <ProtectedRoute
                        exact={true}
                        canAccess={true}
                        path='/public/employerRep/:employerRepId/sign-up/set-password'
                        component={RegistrationPageStepTwo}
                    />
                    <ProtectedRoute
                        exact={true}
                        canAccess={isUserAuthorized}
                        doExtraCheck={false}
                        path={RoutePaths.talenyaCreateRecruiter}
                        component={CreateRecruiterPageForAllowedUsers}
                    />
                    <ProtectedRoute
                        exact={true}
                        allowFromBackend={true}
                        canAccess={isUserAuthorized}
                        path='/print/:requesterUserType/:requesterUserId/position/:positionId/workflowStep/:workflowStep/:profileType/:talentId'
                        component={PrintableCandidateProfilePage}
                    />
                    <ProtectedRoute
                        exact={true}
                        canAccess={isUserAuthorized}
                        path='/print/employer/:employerId/position/:positionId/diversityOptimizer'
                        component={PrintableDiversityOptimizerPage}
                    />
                    <ProtectedRoute
                        exact={true}
                        canAccess={isUserAuthorized}
                        path='/print/employer/:employerId/diversityReport'
                        component={PrintableDiversityReportPage}
                    />
                    <ProtectedRoute
                        canAccess={isUserAuthorized}
                        exact={true}
                        path='/employer/:employerId/editEmployer'
                        component={EditEmployerPage}
                    />
                    <ProtectedRoute
                        canAccess={isUserAuthorized}
                        forSuperUsersOnly={true}
                        exact={true}
                        path='/employer/:employerId/editEmployer/defaultAssociatedEmployersSettings'
                        component={EditAdvancedSettingsPage}
                    />
                    <ProtectedRoute
                        canAccess={isUserAuthorized}
                        path='/employer/:employerId/welcome'
                        component={EmployerWelcomePage}
                    />
                    <ProtectedRoute
                        canAccess={isUserAuthorized}
                        path='/employer/:employerId/campaignManager'
                        component={EmployerCampaignManagerPage}
                    />
                    <ProtectedRoute
                        canAccess={isUserAuthorized}
                        path='/employer/:employerId/campaignsActivity'
                        component={EmployerCampaignsActivityPage}
                    />
                    <ProtectedRoute
                        path='/employer/:employerId/PcCampaignsActivity'
                        canAccess={isUserAuthorized}
                        component={PCCampaignActivityPage}
                    />
                    <ProtectedRoute
                        canAccess={isUserAuthorized}
                        path='/employer/:employerId/position/:positionId/campaignsActivity'
                    >
                        <CampaignApp>
                            <EmployerCampaignActivityPage/>
                        </CampaignApp>
                    </ProtectedRoute>
                    <ProtectedRoute
                        canAccess={isUserAuthorized}
                        forSuperUsersOnly={true}
                        path='/employer/:employerId/manageUsers'
                        component={EmployerRepManageUsersPage}
                    />
                    <ProtectedRoute
                        canAccess={isUserAuthorized}
                        path='/employer/:employerId/diversityReport'
                        component={EmployerRepDiversityReportPage}
                    />
                    <ProtectedRoute
                        canAccess={isUserAuthorized}
                        doExtraCheck={false}
                        path='/employer/:employerId/position/wizard'
                        component={EmployerJobWizardPage}
                    />
                    <ProtectedRoute
                        canAccess={isUserAuthorized}
                        doExtraCheck={false}
                        path='/employer/:employerId/position/:positionId/wizard'
                        component={EmployerJobWizardPage}
                    />
                    <ProtectedRoute
                        canAccess={isUserAuthorized}
                        doExtraCheck={false}
                        path='/employer/:employerId/position/:positionId/unknownPage'
                        component={EmployerJobWizardResolverPage}
                    />
                    <ProtectedRoute
                        canAccess={true}
                        doExtraCheck={false}
                        path='/employer/:employerId/position/:positionId/calibration-disabled-page'
                        component={CalibrationDisabledPage}
                    />
                    <ProtectedRoute
                        canAccess={true}
                        doExtraCheck={false}
                        path='/employer/:employerId/position/:positionId/calibration-not-ready-page'
                        component={CalibrationNotReadyPage}
                    />
                    <ProtectedRoute
                        canAccess={isUserAuthorized}
                        path='/employer/:employerId/position/:positionId/:positionSection'
                        component={EmployerPositionPage}
                    />
                    <ProtectedRoute
                        canAccess={isUserAuthorized}
                        path={employerPositionsPath}
                        component={EmployerPositionsPage}
                    />
                    <ProtectedRoute
                        canAccess={true}
                        path='/public/position-description/:positionId'
                        component={PublicJobDescriptionPage}
                    />
                    <ProtectedRoute
                        canAccess={true}
                        path='/public/position-approval/:positionApprovalUuid'
                        component={PublicJobApprovalPage}
                    />
                    <ProtectedRoute
                        canAccess={true}
                        path='/public/diversityReport/:employerId'
                        component={PublicDiversityReportPage}
                    />
                    <ProtectedRoute
                        canAccess={isUserAuthorized}
                        path='/position/:positionId/search'
                        component={SearchCandidatesPage}
                    />
                    <ProtectedRoute
                        canAccess={true}
                        path='/public/unsubscribe-me/:positionApprovalUuid/:messageUuid'
                        component={UnsubscribeOptionsPage}
                    />
                    <ProtectedRoute
                        canAccess={true}
                        path='/public/thank-you'
                        component={ThankYouPage}
                    />
                    <ProtectedRoute
                        canAccess={true}
                        path='/employer/:employerId/employerCampaigns'
                        component={EmployerCampaignsPage}
                    />
                    <ProtectedRoute
                        canAccess={true}
                        path='/employer/:employerId/campaign/:campaignId'
                        component={EmployerCampaignDetailsPage}
                    />
                    <ProtectedRoute
                        canAccess={true}
                        path='/employer/:employerId/campaign/create'
                        component={EmployerCampaignDetailsPage}
                    />
                    <ProtectedRoute
                        canAccess={true}
                        path='/public/position-approval-v2/:positionApprovalUuid'
                        component={JobLandingPageContainer}
                    />
                    <ProtectedRoute
                        canAccess={true}
                        path='/public/position-description-v2/:positionId'
                        component={JobLandingPageDescriptionContainer}
                    />
                    <ProtectedRoute
                        canAccess={true}
                        doExtraCheck={false}
                        path='/no-access-page'
                        component={NoAccessPage}
                    />
                    <ProtectedRoute
                        canAccess={true}
                        doExtraCheck={false}
                        path='/employer/:employerId/no-access-page'
                        component={NoAccessPage}
                    />
                    <ProtectedRoute
                        canAccess={true}
                        doExtraCheck={false}
                        path='/inactive-user-page'
                        component={InactiveUserPage}
                    />
                    <ProtectedRoute
                        canAccess={true}
                        path='/public/unsubscribe/:positionApprovalUuid/:messageUuid'
                        component={UnsubscribePageContainer}
                    />
                    <ProtectedRoute
                        canAccess={true}
                        doExtraCheck={false}
                        path='/sso-login-expired-page'
                        component={LoginSSOExpiredPage}
                    />
                    <ProtectedRoute
                        canAccess={true}
                        doExtraCheck={false}
                        path='/successful-activation-page'
                        component={SuccessfulActivationPage}
                    />
                    <ProtectedRoute
                        canAccess={true}
                        doExtraCheck={false}
                        path='/employer/:employerId/successful-activation-page'
                        component={SuccessfulActivationPage}
                    />
                    <ProtectedRoute
                        canAccess={isUserAllowedToEditSystemCampaigns}
                        path='/employer/:employerId/system/campaign/:campaignId'
                        component={SystemCampaignDetailsPage}
                    />
                    <Redirect
                        from='/employer/:employerId/position/:positionId/feedbackWizard'
                        to='/employer/:employerId/position/:positionId/wizard/feedback'
                    />
                    <Redirect
                        from='/'
                        to='/login'
                    />
                </Switch>
            </Suspense>
        </ErrorBoundary>
    );
};

export default ApplicationRoutes;
